import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const PricingLandingPage = () => {
    const [billingCycle, setBillingCycle] = useState('monthly'); // monthly or yearly
    const [openFaq, setOpenFaq] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const backgroundImages = [
        '/padel_arena.png',
        '/padel_celebration.png',
        '/padel_highlight.png',
        '/padel_action.png'
    ];

    useEffect(() => {
        document.title = 'Planes y Precios - VisioPadel';
        const interval = setInterval(() => {
            setCurrentImageIndex((prevIndex) => (prevIndex + 1) % backgroundImages.length);
        }, 6000);
        return () => clearInterval(interval);
    }, []);

    const plans = [
        {
            name: 'Jugador',
            price: 0,
            period: 'Gratis',
            description: 'Para jugadores que quieren participar en eventos',
            features: [
                'Participar en eventos ilimitados',
                'Ver rankings y estadísticas',
                'Historial de partidos',
                'Notificaciones de eventos',
                'Perfil de jugador'
            ],
            cta: 'Registrarse Gratis',
            ctaLink: '/signup?plan=player',
            popular: false,
            color: 'slate'
        },
        {
            name: 'Organizador',
            price: billingCycle === 'monthly' ? 29 : 290,
            period: billingCycle === 'monthly' ? '/mes' : '/año',
            description: 'Para organizadores independientes de eventos',
            trial: '14 días de prueba gratis',
            features: [
                'Todo lo del plan Jugador',
                'Crear eventos ilimitados',
                'Gestión en vivo de partidos',
                'Cobros integrados',
                'Branding personalizado',
                'Cuadros automáticos',
                'Soporte por email'
            ],
            cta: 'Comenzar Prueba Gratis',
            ctaLink: '/signup?plan=organizer',
            popular: true,
            color: 'primary'
        },
        {
            name: 'Club',
            price: null,
            period: null,
            description: 'Para instalaciones deportivas con múltiples gestores',
            features: [
                'Todo lo del plan Organizador',
                'Múltiples gestores',
                'Gestión de instalaciones',
                'Sistema de reservas',
                'API personalizada',
                'Soporte prioritario 24/7'
            ],
            cta: 'Próximamente',
            ctaLink: '#',
            popular: false,
            comingSoon: true,
            color: 'blue'
        }
    ];

    const testimonials = [
        {
            name: 'Carlos Martínez',
            role: 'Organizador en Madrid',
            image: '👨‍💼',
            quote: 'VisioPadel me ha ahorrado horas de trabajo cada semana. Antes pasaba todo el día con Excel, ahora todo es automático.'
        },
        {
            name: 'Ana García',
            role: 'Club Padel Premium',
            image: '👩‍💼',
            quote: 'La mejor inversión para nuestro club. Los jugadores están encantados con la experiencia profesional que ofrecemos ahora.'
        }
    ];

    const faqs = [
        {
            question: '¿Puedo cancelar en cualquier momento?',
            answer: 'Sí, puedes cancelar tu suscripción en cualquier momento desde tu dashboard. No hay permanencia ni penalizaciones.'
        },
        {
            question: '¿Qué incluye la prueba gratuita de 14 días?',
            answer: 'Tienes acceso completo a todas las funcionalidades del plan Organizador durante 14 días.'
        },
        {
            question: '¿Cómo funcionan los cobros a los jugadores?',
            answer: 'Usamos Stripe para procesar los pagos. Los jugadores pagan con tarjeta y el dinero llega directamente a tu cuenta.'
        },
        {
            question: '¿Puedo cambiar de plan más adelante?',
            answer: 'Por supuesto. Puedes actualizar o degradar tu plan en cualquier momento desde la configuración de tu cuenta.'
        }
    ];

    return (
        <div className="min-h-screen bg-slate-50 font-sans">
            {/* Navbar */}
            <nav className="fixed top-0 left-0 w-full z-50 bg-[#0f172a]/95 backdrop-blur-md h-20 md:h-24 transition-all border-b border-white shadow-lg">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
                    <div className="flex justify-between items-center h-full">
                        <Link to="/" className="flex items-center group">
                            <img 
                                src="/assets/SVG/LogoVisioPadelBlanco.svg?v=2" 
                                alt="VisioPadel" 
                                className="h-14 md:h-20 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                            />
                        </Link>

                        <div className="hidden md:flex items-center gap-8 text-[15px] font-light text-slate-200">
                             <div className="flex flex-col items-center opacity-60 cursor-not-allowed">
                                <div className="flex items-center gap-1.5 whitespace-nowrap text-slate-400">
                                    Gestiona tu club
                                </div>
                                <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border border-slate-700 -mt-1">Próximamente</span>
                            </div>
                            <Link to="/pricing" className="nav-link-xporty text-white font-medium transition-colors active">Organiza tu evento</Link>
                            <Link to="/about" className="nav-link-xporty hover:text-white transition-colors">Cómo Funciona</Link>
                            <Link to="/login" className="nav-link-xporty hover:text-white transition-colors">Entrar</Link>
                            <Link to="/signup" className="nav-link-xporty hover:text-white transition-colors">Registrarse</Link>
                        </div>



                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="md:hidden p-2 text-white"
                        >
                             <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                            </svg>
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="relative pt-24 pb-32 md:pt-36 md:pb-48 overflow-hidden">
                 <div className="absolute inset-0 z-0">
                     {backgroundImages.map((image, index) => (
                        <div
                            key={image}
                            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-[2000ms] ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'}`}
                            style={{ backgroundImage: `url('${image}')` }}
                        ></div>
                    ))}
                    <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-[2px]"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
                    <span className="inline-block py-1 px-3 rounded-full bg-white/10 text-white/90 backdrop-blur-md border border-white/20 text-sm font-semibold mb-4 tracking-wide uppercase">
                        Planes Flexibles
                    </span>
                    <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 tracking-tight leading-none drop-shadow-lg">
                        Organiza como un <span className="text-[var(--primary-brand)]">Profesional</span>
                    </h1>
                    <p className="text-xl text-slate-200 mb-10 max-w-3xl mx-auto font-medium leading-relaxed drop-shadow-md">
                        Comienza gratis y actualiza cuando necesites más potencia. Sin contratos ocultos.
                    </p>

                    {/* Billing Toggle */}
                    <div className="inline-flex items-center bg-slate-800/80 backdrop-blur-md border border-slate-700 rounded-full p-1 shadow-2xl mb-12">
                        <button
                            onClick={() => setBillingCycle('monthly')}
                            className={`px-6 py-2 rounded-full font-bold transition-all ${
                                billingCycle === 'monthly'
                                    ? 'bg-[var(--primary-brand)] text-white shadow-lg'
                                    : 'text-slate-400 hover:text-white'
                            }`}
                        >
                            Mensual
                        </button>
                        <button
                            onClick={() => setBillingCycle('yearly')}
                            className={`px-6 py-2 rounded-full font-bold transition-all ${
                                billingCycle === 'yearly'
                                    ? 'bg-[var(--primary-brand)] text-white shadow-lg'
                                    : 'text-slate-400 hover:text-white'
                            }`}
                        >
                            Anual <span className="text-[10px] ml-1 bg-white/20 px-1.5 py-0.5 rounded text-white">Ahorra 17%</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Pricing Cards Section */}
            <div className="max-w-7xl mx-auto px-4 -mt-32 relative z-20 pb-20">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {plans.map((plan, index) => (
                        <div 
                            key={index} 
                            className={`rounded-[2.5rem] p-8 md:p-10 shadow-2xl border-2 transition-all duration-300 transform hover:-translate-y-2 flex flex-col ${
                                plan.popular 
                                    ? 'bg-[#0f172a] border-[var(--primary-brand)] shadow-[var(--primary-brand)]/20 relative' 
                                    : 'bg-white border-white/50 hover:border-slate-200 shadow-xl'
                            }`}
                        >
                            {plan.popular && (
                                <div className="absolute -top-5 left-1/2 transform -translate-x-1/2">
                                    <span className="bg-[var(--primary-brand)] text-white px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wider shadow-lg">
                                        Más Popular
                                    </span>
                                </div>
                            )}

                            <div className="mb-6">
                                <h3 className={`text-2xl font-black mb-2 ${plan.popular ? 'text-white' : 'text-slate-900'}`}>
                                    {plan.name}
                                </h3>
                                <p className={`text-sm ${plan.popular ? 'text-slate-300' : 'text-slate-500'}`}>
                                    {plan.description}
                                </p>
                            </div>

                            <div className="mb-8 min-h-[4rem]">
                                {plan.price !== null ? (
                                    <div className="flex items-baseline gap-1">
                                        <span className={`text-5xl font-black ${plan.popular ? 'text-white' : 'text-slate-900'}`}>
                                            {plan.price}€
                                        </span>
                                        <span className={`text-lg font-medium ${plan.popular ? 'text-slate-400' : 'text-slate-500'}`}>
                                            {plan.period}
                                        </span>
                                    </div>
                                ) : (
                                    <span className={`text-3xl font-bold italic ${plan.popular ? 'text-slate-400' : 'text-slate-400'}`}>
                                        Consultar
                                    </span>
                                )}
                            </div>

                            {/* Features */}
                            <div className="flex-grow space-y-4 mb-8">
                                {plan.features.map((feature, i) => (
                                    <div key={i} className="flex items-start gap-3">
                                        <div className={`mt-1 flex-shrink-0 ${plan.popular ? 'text-[var(--primary-brand)]' : 'text-[#72c02c]'}`}>
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <span className={`text-sm ${plan.popular ? 'text-slate-300' : 'text-slate-600'}`}>
                                            {feature}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-auto">
                                {plan.comingSoon ? (
                                    <button disabled className="w-full py-4 rounded-xl font-bold bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200">
                                        {plan.cta}
                                    </button>
                                ) : (
                                    <Link
                                        to={plan.ctaLink}
                                        className={`w-full block text-center py-4 rounded-xl font-bold transition-all shadow-lg ${
                                            plan.popular 
                                                ? 'bg-gradient-to-r from-[#72c02c] to-[#65a30d] text-white hover:shadow-[#72c02c]/40 hover:scale-105' 
                                                : 'bg-slate-900 text-white hover:bg-slate-800'
                                        }`}
                                    >
                                        {plan.cta}
                                    </Link>
                                )}
                                {plan.trial && (
                                    <p className={`text-xs text-center mt-3 font-medium ${plan.popular ? 'text-green-400' : 'text-green-600'}`}>
                                        {plan.trial}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Testimonials */}
            <div className="py-20 bg-white border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-4 text-center mb-12">
                     <span className="inline-block py-1 px-3 rounded-full bg-slate-100 text-slate-600 text-sm font-bold uppercase tracking-wide mb-4">
                        Testimonios
                    </span>
                    <h2 className="text-4xl font-extrabold text-slate-900">Lo que dicen de nosotros</h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto px-4">
                    {testimonials.map((testimonial, index) => (
                        <div key={index} className="bg-slate-50 hover:bg-white rounded-[2rem] p-8 border border-slate-200 transition-all shadow-sm hover:shadow-lg">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="text-5xl bg-white p-3 rounded-2xl shadow-sm">{testimonial.image}</div>
                                <div>
                                    <h4 className="font-bold text-slate-900 text-lg">{testimonial.name}</h4>
                                    <p className="text-slate-500 text-sm">{testimonial.role}</p>
                                </div>
                            </div>
                            <p className="text-slate-700 leading-relaxed text-lg font-medium">"{testimonial.quote}"</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* FAQ Section */}
            <div className="py-20 bg-slate-50">
                <div className="max-w-4xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-extrabold text-slate-900 mb-4">
                            Preguntas Frecuentes
                        </h2>
                        <p className="text-lg text-slate-500">Resolvemos tus dudas antes de empezar</p>
                    </div>

                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div key={index} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                <button
                                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                                    className="w-full px-8 py-6 text-left flex items-center justify-between bg-white text-slate-900 font-bold text-lg"
                                >
                                    <span className="pr-4">{faq.question}</span>
                                    <svg
                                        className={`w-6 h-6 text-[var(--primary-brand)] transition-transform duration-300 ${
                                            openFaq === index ? 'transform rotate-180' : ''
                                        }`}
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>
                                <div 
                                    className={`px-8 text-slate-600 leading-relaxed overflow-hidden transition-all duration-300 ease-in-out ${
                                        openFaq === index ? 'max-h-40 pb-8 opacity-100' : 'max-h-0 opacity-0'
                                    }`}
                                >
                                    {faq.answer}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Final CTA */}
            <div className="py-24 bg-[#0f172a] relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[var(--primary-brand)]/20 rounded-full blur-[120px]"></div>
                 <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#0f172a] rounded-full blur-[100px] z-10"></div>
                 
                <div className="container mx-auto px-4 text-center relative z-20">
                    <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                        ¿Listo para profesionalizar tus torneos?
                    </h2>
                    <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
                        Únete a la mejor plataforma de gestión de pádel amateur.
                    </p>
                    <Link
                        to="/signup?plan=organizer"
                        className="inline-block bg-[var(--primary-brand)] hover:bg-[#65a30d] text-white font-bold py-5 px-12 rounded-2xl text-lg shadow-xl shadow-[var(--primary-brand)]/30 transition-all transform hover:-translate-y-1 hover:scale-105"
                    >
                        Comenzar Gratis
                    </Link>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-white border-t border-slate-200 py-12">
                <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-8">
                    <img src="/visiopadel-logo.png" alt="VisioPadel" className="h-10 opacity-70 grayscale hover:grayscale-0 transition-all" />
                    <p className="text-slate-400 text-sm font-bold uppercase tracking-widest">© 2026 VisioPadel</p>
                </div>
            </footer>
        </div>
    );
};

export default PricingLandingPage;
